import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import useMaskedInput, { getClean } from 'hooks/useMaskedInput';
import { BoardSoil } from 'features/Croptimizer/types';
import { TypeOfKey } from 'utils/types';
import useCroptimizerSoil from 'api/hooks/useCroptimizerSoil';
import { BoardSteps, Metric } from 'utils/constants';
import {
  cmToFineDry,
  mtHaToTonAc, numericValues, unMaskInputValue,
} from 'utils/helpers';
import { useNextHandler } from 'features/Croptimizer/providers/NextHandlerContext';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';
import {
  getNotModifiedTotalMtha, getModifiedTotalMtha, getP39Value,
} from 'features/Croptimizer/utils/calcHelperBoard';

// INFO
// CrOptimizer-Simplification task
// nv removed from the equation

const nv = '100';
const cleannv = '100';

let preferredDefaultSoilIndex: number | undefined;

export const useStatusBoard = (options?: { defaultSoilIndex?: number }) => {
  const defaultSoilIndex = options?.defaultSoilIndex ?? 0;

  if (typeof options?.defaultSoilIndex === 'number') {
    preferredDefaultSoilIndex = options.defaultSoilIndex;
  }

  const {
    setStep,
    setBoardStatusValues,
    setBoardSoilValues,
    boardSoilValues,
    boardStatusValues,
    step,
    metricValue,
    kg,
    mtHa,
    efficiency,
  } = useCroptimizer();

  const { t } = useTranslation();
  const isActive = step === BoardSteps.status;
  const { setDisabled, setHandler, setShowBack, setShowNext } =
    useNextHandler();

  const { data: soils } = useCroptimizerSoil();

  const changeBoardSoilValues = useCallback(
    (name: keyof BoardSoil) => (value: TypeOfKey<BoardSoil>) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardSoilValues((prevValues: BoardSoil | null) => ({
        ...prevValues,
        [name]: value,
      }));
    },
    [],
  );

  const soilSelect = useMemo(() => {
    if (soils) {
      return soils?.map((soil) => ({
        value: soil.Soils,
        label: soil.Soils,
      }));
    }

    return [];
  }, [soils]);

  const getDefaultSoilValue = () => {
    const index = preferredDefaultSoilIndex ?? defaultSoilIndex;

    return soilSelect[index]?.value ?? soilSelect[0]?.value ?? '';
  };

  const { value: soilType, setValue: setSoilType } = useMaskedInput({
    defaultValue: getDefaultSoilValue(),
    value: boardSoilValues?.soilType,
    onChangeCallback: (value) => {
      changeBoardSoilValues('soilType')(value);
      setSecSmol(
        soils?.find((soil) => soil.Soils === value)?.CEC.toString() ?? '',
      );
    },
    isDisableClean: true,
  });

  const {
    value: currentph,
    setValue: setCurrentPh,
    clean: cleancurrentph,
  } = useMaskedInput({
    defaultValue: boardStatusValues?.currentPh ?? '6,00',
    value: boardStatusValues?.currentPh ?? '6,00',
    onChangeCallback: (value) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardStatusValues((pv) => ({
        ...pv,
        currentPh: getClean(value),
      }));
    },
  });

  const {
    value: currentSec,
    setValue: setCurrentSec,
    clean: cleancurrentsec,
  } = useMaskedInput({
    defaultValue: '70',
    value: boardStatusValues?.currentSec ?? '',
    onChangeCallback: (value) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardStatusValues((pv) => ({
        ...pv,
        currentSec: value,
      }));
    },
  });

  const {
    value: targetSec,
    setValue: setTargetSec,
    clean: cleantargetsec,
  } = useMaskedInput({
    defaultValue: boardStatusValues?.targetSec ?? '',
    value: boardStatusValues?.targetSec ?? '',
    onChangeCallback: (value) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardStatusValues((pv) => ({
        ...pv,
        targetSec: value,
      }));
    },
  });

  const {
    value: targetph,
    setValue: setTargetPh,
    clean: cleantargetph,
  } = useMaskedInput({
    defaultValue: boardStatusValues?.targetPh ?? '6,50',
    value: boardStatusValues?.targetPh ?? '6,50',
    onChangeCallback: (value) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardStatusValues((pv) => ({
        ...pv,
        targetPh: getClean(value),
      }));
    },
  });

  const isGreaterTarget = useMemo(() => {
    if (boardStatusValues) {
      return Number(cleancurrentph) < Number(cleantargetph);
    }

    return false;
  }, [boardStatusValues, cleancurrentph, cleantargetph]);

  const isMaintenanceMode = useMemo(
    () => Number(cleancurrentph) === Number(cleantargetph),
    [cleancurrentph, cleantargetph],
  );

  const {
    value: secSmol,
    setValue: setSecSmol,
    clean: cleansecsmol,
  } = useMaskedInput({
    defaultValue: '',
    value: boardSoilValues?.secsmol ?? '',
    // onChangeCallback: changeBoardSoilValues('secsmol'),
    onChangeCallback: (value) => {
      changeBoardSoilValues('secsmol')(value);
    },

  });

  const { value: initialSecSmol } = useMaskedInput({
    defaultValue: '',
    value: boardSoilValues?.initialSecsmol ?? '',
    onChangeCallback: changeBoardSoilValues('initialSecsmol'),
    isDisableClean: true,
  });

  const {
    value: fineDrySoil,
    setValue: setFineDrySoil,
    clean: cleanfineDrySoil,
  } = useMaskedInput({
    defaultValue:
      metricValue === Metric.metric
        ? '20'
        : unMaskInputValue(String(mtHaToTonAc(20))),
    value:
      boardSoilValues?.fineDrySoil ??
      (metricValue === Metric.metric
        ? '20'
        : unMaskInputValue(String(mtHaToTonAc(20)))),
    onChangeCallback: changeBoardSoilValues('fineDrySoil'),
  });

  const {
    value: dose,
    setValue: setDose,
    clean: cleandose,
  } = useMaskedInput({
    defaultValue: '100',
    value: boardSoilValues?.dose ?? '100',
    onChangeCallback: (value) => {
      changeBoardSoilValues('dose')(getClean(value));
    },
  });

  const errorDose = useMemo(() => {
    if (Number(cleandose) < 10) {
      return t('minimumPossibleValue') + ' 10%';
    }

    if (Number(cleandose) > 300) {
      return t('maximumPossibleValue') + ' 300%';
    }

    return null;
  }, [dose]);

  const { value: split, setValue: setSplit } = useMaskedInput({
    value: boardSoilValues?.split ?? '1',
    defaultValue: '1',
    onChangeCallback: changeBoardSoilValues('split'),
    isDisableClean: true,
  });

  const getOptimumValue = useMemo(() => {
    if (boardStatusValues && efficiency?.length) {
      const targetSec = Number(numericValues(boardStatusValues?.targetSec));

      // Filter out items with null CAP values
      const validEfficiency = efficiency.filter((item) => item.CAP !== null);

      if (validEfficiency.length === 0) {
        return '';
      }

      if (targetSec < 40) {
        return String(
          validEfficiency.reduce((acc, loc) => (acc.CAP < loc.CAP ? acc : loc))
            .CAP,
        );
      }

      if (targetSec > 150) {
        return String(
          validEfficiency.reduce((acc, loc) => (acc.CAP > loc.CAP ? acc : loc))
            .CAP,
        );
      }

      return String(
        validEfficiency.reduce((a, b) => Math.abs(b.CAP - targetSec) < Math.abs(a.CAP - targetSec) ? b : a,
        ).CAP,
      );
    }

    return '';
  }, [boardStatusValues, efficiency]);

  /*   const { value: productType } = useMaskedInput({
    defaultValue: '',
    value: boardSoilValues?.productName ?? '',
    onChangeCallback: (value) => {
      changeBoardSoilValues('productName')(value);
    },
    isDisableClean: true,
  }); */

  /*   const getSelectedValue = useMemo(() => {
    if (productType?.length > 0 && products) {
      const getProduct = products?.find(
        (product) => product.Products === productType,
      );

      return String(getProduct?.CAP);
    }

    return '';
  }, [productType, products]); */

  const [errorCurrent, setErrorCurrent] = useState('');
  const [errorTarget, setErrorTarget] = useState('');
  const [errorTargetSec, setErrorTargetSec] = useState('');
  const [currentSecAuto, setCurrenSecAuto] = useState('');
  const syncedCurrentPh = useRef<number | null>(null);

  const disabledState = useMemo(
    () => errorCurrent.length > 0 ||
      errorTarget.length > 0 ||
      Number(cleantargetph) < Number(cleancurrentph) ||
      !!(errorDose && errorDose?.length > 0),
    [
      errorCurrent.length,
      errorTarget.length,
      cleantargetph,
      cleancurrentph,
      errorDose?.length,
    ],
  );

  useEffect(() => {
    if (Number(cleancurrentph) < 4.88 && currentph.length >= 0) {
      setErrorCurrent(t('minimumPossibleValue') + ' 4,89');
      setCurrentSec('');

      return;
    }

    if (Number(cleancurrentph) > 7.56 && currentph.length > 0) {
      setErrorCurrent(t('maximumPossibleValue') + ' 7,56');
      setCurrentSec('');

      return;
    }

    if (Number(cleancurrentph) < 7.56 && Number(cleancurrentph) > 4.88) {
      setErrorCurrent('');
      const result =
        Math.round(
          (Math.tan((Number(cleancurrentph) - 5.8) / 1.45) * 54 + 70) /
          100 /
          0.05,
        ) * 5;
      const phValue = Number(cleancurrentph);
      const phChanged = syncedCurrentPh.current !== null && syncedCurrentPh.current !== phValue;
      syncedCurrentPh.current = phValue;
      setCurrenSecAuto(String(result));
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardStatusValues((pv) => {
        const savedSec = Number(getClean(pv?.currentSec ?? ''));
        const keepManualSec = !phChanged
          && Boolean(pv?.currentSec)
          && !Number.isNaN(savedSec)
          && savedSec !== result;

        if (keepManualSec) {
          if (pv?.initialCurrentSec === String(result)) {
            return pv;
          }

          return {
            ...pv,
            initialCurrentSec: String(result),
          };
        }

        return {
          ...pv,
          currentSec: `${result} %`,
          initialCurrentSec: String(result),
        };
      });
    }
  }, [currentph, cleancurrentph]);

  useEffect(() => {
    if (Number(cleantargetph) < 4.88 && targetph.length >= 0) {
      setErrorTarget(t('minimumPossibleValue') + ' 4,89');
      setTargetSec('');

      return;
    }

    if (targetph && Number(cleantargetph) > 7.56) {
      setErrorTarget(t('maximumPossibleValue') + ' 7,56');
      setTargetSec('');

      return;
    }

    if (Number(cleantargetph) < Number(cleancurrentph)) {
      setErrorTarget(
        'La valeur du statut cible ne peut pas être inférieure à la valeur du statut actuel.',
      );

      return;
    }

    if (
      Number(cleantargetph) < 7.56 &&
      Number(cleantargetph) > 4.88 &&
      Number(cleantargetph) >= Number(cleancurrentph)
    ) {
      setErrorTarget('');
      const result =
        Math.round(
          (Math.tan((Number(cleantargetph) - 5.8) / 1.45) * 54 + 70) /
          100 /
          0.05,
        ) * 5;
      setTargetSec(String(result) + ' %');
    }
  }, [targetph, currentph]);

  useEffect(() => {
    if (Number(cleantargetsec) < 0) {
      setErrorTargetSec(t('minimumPossibleValue') + ' is 0');
    }

    if (Number(cleantargetsec) > 300) {
      setErrorTargetSec(t('maximumPossibleValue') + ' is 300');
    }
  }, [targetSec, targetph]);

  const errorCurrentSecHandle = useMemo(() => {
    if (Number(cleancurrentsec) < 0) {
      return t('minimumPossibleValue') + ' is 0';
    }

    if (Number(cleancurrentsec) > 300) {
      return t('maximumPossibleValue') + ' is 300';
    }

    return '';
  }, [currentSec]);

  const getMthaText = useMemo(() => {
    if (cleancurrentph && cleantargetph) {
      const text = `Total VN (CaO) ${'in'} ${mtHa}*`;

      /* const text = isGreaterTarget
        ? `Total ${mtHa}*` + split
        : 'Kg/ha/yr/' + split; */

      return text;
    }

    return '';
  }, [boardStatusValues, split]);

  const getTotalMtha = useMemo(() => {
    if (cleancurrentph && cleantargetph) {
      const CECSoil = Number(cleansecsmol || 0);
      const FineAndDry = cmToFineDry(Number(fineDrySoil ?? '20'));
      const NV = nv.length > 0 ? Number(cleannv) : 1;
      const TargetStatus = Number(cleantargetph);
      const CurrentStatus = Number(cleancurrentph);
      const Dose = Number(cleandose) / 100;

      if (isGreaterTarget) {
        const currentSecNumber = Number(cleancurrentsec);
        const usesBaseFormula = currentSecNumber === Number(currentSecAuto);

        if (usesBaseFormula) {
          return getNotModifiedTotalMtha(
            CECSoil,
            FineAndDry,
            NV,
            TargetStatus,
            CurrentStatus,
            Dose,
          );
        }

        return getModifiedTotalMtha(
          currentSecNumber,
          CurrentStatus,
          CECSoil,
          FineAndDry,
          NV,
          TargetStatus,
          Dose,
        );
      } else {
        return String(getP39Value(CECSoil, NV, Dose));
      }
    }

    return '';
  }, [
    cleancurrentph,
    cleancurrentsec,
    cleantargetph,
    cleandose,
    fineDrySoil,
    nv,
    cleansecsmol,
    isGreaterTarget,
    currentSecAuto,
  ]);

  const getMtha = useMemo(() => {
    if (getTotalMtha && split && boardStatusValues) {
      const text = isGreaterTarget
        ? String(Number(getTotalMtha) / Number(split))
        : String(Number(getTotalMtha) * Number(split));

      return text;
    }

    return '';
  }, [getTotalMtha, split, boardStatusValues]);

  // Save totalMtha after getTotalMtha updates (fixes race condition)
  useEffect(() => {
    if (getTotalMtha) {
      changeBoardSoilValues('totalMtha')(String(Number(getTotalMtha)));
    }
  }, [getTotalMtha]);

  const resetData = () => {
    const defaultSoilValue = getDefaultSoilValue();
    setSoilType(defaultSoilValue);
    setSecSmol(
      soils
        ?.find((soil) => soil.Soils === defaultSoilValue)
        ?.CEC.toString() ?? '',
    );
    setCurrentPh('6,00');
    setTargetPh('6,50');
    setCurrentSec('75');
    setFineDrySoil('20');
    setDose('100');
    setSplit('1');
  };

  const errorSecSmol = useMemo(() => {
    if (Number(cleansecsmol) < 2) {
      return t('minimumPossibleValue') + ' 2';
    }

    if (Number(cleansecsmol) > 200) {
      return t('maximumPossibleValue') + ' 200';
    }

    return null;
  }, [secSmol]);

  const handleFineDrySoil = useMemo(() => {
    if (Number(cleanfineDrySoil) < 5) {
      return t('minimumPossibleValue') + ' 5';
    }

    if (Number(cleanfineDrySoil) > 50) {
      return t('maximumPossibleValue') + ' 50';
    }

    return null;
  }, [fineDrySoil]);

  useEffect(() => {
    if (!soilType && soilSelect && soilSelect.length > 0 && !boardSoilValues?.soilType) {
      const defaultSoilValue = getDefaultSoilValue();

      if (defaultSoilValue) {
        setSoilType(defaultSoilValue);
      }
    }
  }, [soilType, soilSelect, boardSoilValues?.soilType, getDefaultSoilValue]);

  useEffect(() => () => {
    if (typeof options?.defaultSoilIndex === 'number' && preferredDefaultSoilIndex === options.defaultSoilIndex) {
      preferredDefaultSoilIndex = undefined;
    }
  }, []);

  useEffect(() => {
    if (soilType && !boardSoilValues?.secsmol) {
      setSecSmol(
        soils?.find((soil) => soil.Soils === soilType)?.CEC.toString() ?? '',
      );
    }
  }, [soilType]);

  useEffect(() => {
    // When active, configure navigation buttons
    // and define the "Next" handler to update board state and trigger status handling
    if (!isActive) {
      return;
    }

    setShowBack(false);
    setShowNext(true);
    setDisabled(disabledState);

    setHandler(() => {
      setBoardStatusValues({
        currentPh: cleancurrentph,
        currentSec: cleancurrentsec,
        targetPh: cleantargetph,
        targetSec: cleantargetsec,
        initialCurrentSec: currentSecAuto,
      });

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardSoilValues((prevState) => ({
        ...prevState,
        soilType,
        nv: cleannv,
        fineDrySoil: fineDrySoil.replace(/'/g, ''),
        selected: '110', // TODO use getSelectedValue()
        totalMtha: String(Number(getTotalMtha)),
        dose: cleandose,
        split,
      }));

      setStep(BoardSteps.crop);
    });
  }, [
    isActive,
    cleandose,
    disabledState,
    cleancurrentph,
    cleancurrentsec,
    cleantargetph,
    cleantargetsec,
    currentSecAuto,
    fineDrySoil,
    soilType,
    split,
  ]);

  return {
    // Data
    soilSelect,
    soilType,
    setSoilType,
    currentph,
    setCurrentPh,
    currentSec,
    setCurrentSec,
    targetSec,
    setTargetSec,
    targetph,
    setTargetPh,
    errorCurrent,
    errorTarget,
    errorTargetSec,
    currentSecAuto,
    secSmol,
    setSecSmol,
    kg,
    mtHa,
    initialSecSmol,
    fineDrySoil,
    setFineDrySoil,
    errorSecSmol,
    handleFineDrySoil,
    errorCurrentSecHandle,
    cleancurrentph,
    cleantargetph,
    dose,
    setDose,
    cleandose,
    errorDose,
    split,
    setSplit,
    isGreaterTarget,
    isMaintenanceMode,
    getMthaText,
    getMtha,
    getTotalMtha,

    // Methods
    resetData,
    getOptimumValue,
  };
}; 